<template>
  <v-row no-gutters class="fill-height flex-column">
    <v-col v-if="mapPanelDataFetched">
      <profiles-widget
        v-if="withProfiles"
        v-bind="profilesData"
        :is-add-profile-disabled="($refs['steps-wizard'] ? $refs['steps-wizard'].allDataValid : false) && allDataValid"
        :current-data="getPayload()"
        v-on="$listeners"
        @changeCurrentProfile="changeCurrentProfile"
      ></profiles-widget>
      <steps-wizard
        ref="steps-wizard"
        :steps="steps"
        :inputs="inputs"
        :params-proxy.sync="params"
        :is-analysis-done-proxy.sync="isAnalysisDone"
        :is-analysis-loading="isAnalysisLoading"
        :wizard-action-handler="turnOnAnalysis"
        :additional-is-data-valid="allDataValid"
        :custom-reset-handler="customResetHandler"
      >
        <template v-for="step in steps" #[`stepAppend-${step.step}`]>
          <slot :name="`stepAppend-${step.step}`"></slot>
        </template>
        <template #stepPrepend-last>
          <slot name="lastStep-prepend"></slot>
          <v-row v-if="useAnalysisDate">
            <v-col cols="6" class="pb-0">
              <data-input
                v-model="params['analysis_date']"
                :data-type="{ name: 'date' }"
                :locale-date-format="
                  analysisDateType === 'year' ? 'YYYY' : analysisDateType === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD'
                "
                :label="$i18n.t('sidebar.scadaFlowCollectionDate')"
                :disabled="isAnalysisLoading"
                :datePickerProps="{ type: analysisDateType }"
                v-bind="{
                  rules: [v => !!v || $i18n.t('rules.required')],
                }"
              />
            </v-col>
          </v-row>
        </template>
        <template #analysisDoneCallback>
          <v-col cols="auto" class="pa-0 pb-5">
            <v-alert dense text type="success" class="ma-0 pa-0 text-left">
              <template #prepend>
                <v-icon aria-hidden="false" class="mr-2" color="success">mdi-check</v-icon>
              </template>
              <span class="text-left text-body-2">{{
                $i18n.t(layersNames.length > 1 ? 'sidebar.layersAddedToProject' : 'sidebar.layerAddedToProject', {
                  name: layersNames.join(', '),
                })
              }}</span>
            </v-alert>
          </v-col>
        </template>
      </steps-wizard>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>

<script>
import MainLoader from '@/components/MainLoader';
import ProfilesWidget from '@/components/ProfilesWidget';

import StepsWizard from '@/components/StepsWizard';

import { get, call } from 'vuex-pathify';

export default {
  name: 'WaterBalanceWizard',
  components: {
    MainLoader,
    StepsWizard,
    ProfilesWidget,
    DataInput: () => import('@/components/DataInput'),
  },
  props: {
    withProfiles: {
      type: Boolean,
      default: false,
    },
    useAnalysisDate: {
      type: Boolean,
      default: false,
    },
    analysisDateType: {
      type: String,
      default: 'date',
    },
    toolName: {
      type: String,
      required: true,
    },
    inputs: {
      type: Array,
      default: () => [],
    },
    steps: {
      type: Array,
      default: () => [],
    },
    paramsProxy: {
      type: Object,
      default: () => ({}),
    },
    profilesData: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    isAnalysisLoading: false,
    isAnalysisDone: false,
    currentProfile: null,
    layersNames: [],
    currentTaskId: null,
  }),
  methods: {
    generateWaterBalanceStatistics: call('waterBalance/generateWaterBalanceStatistics'),
    generateWaterBalanceDma: call('waterBalance/generateWaterBalanceDma'),
    checkAnalysisStatus: call('celeryTasks/getCeleryTaskStatus'),
    getResult: call('celeryTasks/getCeleryTaskResult'),
    cancelAnalysis: call('celeryTasks/stopCeleryTask'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    getLayers: call('layers/getLayers'),
    getCurrentUser: call('users/getCurrentUser'),
    async turnOnAnalysis() {
      this.isAnalysisDone = false;
      this.isAnalysisLoading = true;
      try {
        const payload = this.getPayload();
        const taskId =
          this.toolName === 'dma'
            ? await this.generateWaterBalanceDma(payload)
            : await this.generateWaterBalanceStatistics({ name: this.toolName, payload });
        this.currentTaskId = taskId;
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            const status = await this.checkAnalysisStatus(taskId);
            if (['REVOKED', 'FAILURE'].includes(status)) {
              this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
              this.$store.set('snackbar/PUSH_ERROR!', {
                message: this.$i18n.t('errors.backend.requestError'),
                timeout: -1,
              });
              this.isAnalysisLoading = false;
              this.currentTaskId = null;
              throw Error(`Celery task status checker: ${status}`);
            }
            return status === 'SUCCESS';
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            const r = await this.getResult({ id: taskId, type: 'data' });
            await this.getLayers();
            const layer = JSON.parse(JSON.stringify(this.layers[r.result.layer]));
            const dmaLayer = r.result.dma_layer ? JSON.parse(JSON.stringify(this.layers[r.result.dma_layer])) : null;
            if (layer) {
              await this.getLayerMetadata({
                dataSource: layer.data_source_name,
                params: { with_attributes_schema: true },
              });
              layer.labels_visible = layer.labels_visible || false;
              layer.style = layer.style_web;
              delete layer.style_web;
              if (dmaLayer) {
                await this.getLayerMetadata({
                  dataSource: dmaLayer.data_source_name,
                  params: { with_attributes_schema: true },
                });
                dmaLayer.labels_visible = dmaLayer.labels_visible || false;
                dmaLayer.style = dmaLayer.style_web;
                delete dmaLayer.style_web;
              }
              await this.getCurrentUser();
              const layers = [
                { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
                ...(dmaLayer
                  ? [{ layer_id: dmaLayer.id, visible: true, opacity: 1, has_permission: true, ...dmaLayer }]
                  : []),
              ];
              this.$store.set('layers/ADD_PROJECT_LAYERS!', layers);
              this.$root.$emit('pushProjectLayers', layers);
              this.layersNames = [layer.name, ...(dmaLayer ? [dmaLayer.name] : [])];
            }
            this.currentTaskId = null;
            this.isAnalysisDone = true;
            this.isAnalysisLoading = false;
          },
          interval: 5000,
        });
      } catch (error) {
        this.isAnalysisLoading = false;
        this.currentTaskId = null;
        console.log('error', error);
      }
    },
    getPayload() {
      let paramsCopy = JSON.parse(JSON.stringify(this.params));
      return Object.fromEntries(
        Object.entries(paramsCopy).filter(([key]) => !this.inputs.find(input => input.name === key)?.skip)
      );
    },
    async changeCurrentProfile(newProfile) {
      if (newProfile) {
        let currentProfileCopy = JSON.parse(JSON.stringify(newProfile));
        delete currentProfileCopy.id;
        delete currentProfileCopy.default;
        delete currentProfileCopy.name;
        await this.$nextTick();
        this.params = { ...this.params, ...currentProfileCopy };
      }
    },
    customResetHandler(paramsBeforeClean) {
      let paramsToRecover = {};
      this.inputs.forEach(input => {
        if (input.isLayer || input.skipFormReset) paramsToRecover[input.name] = paramsBeforeClean[input.name];
      });
      this.$nextTick(() => {
        Object.entries(paramsToRecover).forEach(([key, value]) => {
          this.params[key] = value;
        });
      });
    },
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    layers: get('layers/layers'),
    params: {
      get() {
        return this.paramsProxy;
      },
      set(nV) {
        this.$emit('update:paramsProxy', nV);
      },
    },
    allDataValid() {
      return this.useAnalysisDate ? !!this.params['analysis_date'] : true;
    },
  },
  beforeDestroy() {
    if (this.currentTaskId) {
      this.cancelAnalysis(this.currentTaskId);
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select__selections {
    flex-wrap: wrap !important;
  }
  .v-alert--text:before {
    opacity: 0;
  }
  .v-input--checkbox {
    margin-top: 0 !important;
    .v-messages__wrapper {
      font-family: Roboto;
      font-style: italic;
      font-weight: 300;
      font-size: 11px;
      padding-left: 34px;
      color: rgba(0, 0, 0, 0.59);
    }
    .v-label {
      font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      text-align: left;
      line-height: 16px;
      color: #202124;
    }
  }
}
</style>
